<script setup lang="ts">
import { computed } from "vue";

import ComfortIcon from "@/assets/icons/programs/comfort.svg?component";
import EcoIcon from "@/assets/icons/programs/eco.svg?component";
import FrostIcon from "@/assets/icons/programs/frost.svg?component";
import PowerIcon from "@/assets/icons/programs/power.svg?component";
import XCircleIcon from "@/assets/icons/programs/x-circle.svg?component";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { applianceCategoryMode, EMetaModeGroup } from "@/data/api/kili/programs/program.type";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { cn } from "@/utils/libs/tailwind-utils";
import {
  EHeaterQuickConfiguration,
  EOnOffApplianceQuickConfiguration,
  TProgramQuickConfiguration,
} from "@/utils/programs/program-presets";

interface IProps {
  applianceCategory: EProgramApplianceCategory;
  activeConfigurations: readonly TProgramQuickConfiguration[];
  disabled?: boolean;
}

interface IEmits {
  (e: "toggle", configuration: TProgramQuickConfiguration): void;
}

const props = withDefaults(defineProps<IProps>(), {
  disabled: false,
});
const emit = defineEmits<IEmits>();
const { t, k } = useTypedIntl();

function isActive(configuration: TProgramQuickConfiguration) {
  return props.activeConfigurations.includes(configuration);
}

const newConfigurations = computed(() => {
  if (applianceCategoryMode[props.applianceCategory] === EMetaModeGroup.HEATER_MODE) {
    return [
      {
        value: EHeaterQuickConfiguration.BUSINESS_DAYS_ECO,
        label: k.control.programming.details.quickConfigurations.weekdaysEco,
        icon: EcoIcon,
        iconName: "eco-leaf",
      },
      {
        value: EHeaterQuickConfiguration.EVERY_DAY_COMFORT,
        label: k.control.programming.details.quickConfigurations.everyDayComfort,
        icon: ComfortIcon,
        iconName: "comfort",
      },
      {
        value: EHeaterQuickConfiguration.NIGHT_NO_FROST,
        label: k.control.programming.details.quickConfigurations.nightFrost,
        icon: FrostIcon,
        iconName: "frost",
      },
    ];
  }

  return [
    {
      value: EOnOffApplianceQuickConfiguration.WEEKEND_OFF,
      label: k.control.programming.details.quickConfigurations.weekendOff,
      icon: XCircleIcon,
      iconName: "x-circle",
    },
    {
      value: EOnOffApplianceQuickConfiguration.EVERY_DAY_ON,
      label: k.control.programming.details.quickConfigurations.everyDayOn,
      icon: PowerIcon,
      iconName: "power",
    },
    {
      value: EOnOffApplianceQuickConfiguration.NIGHT_OFF,
      label: k.control.programming.details.quickConfigurations.nightOff,
      icon: XCircleIcon,
      iconName: "x-circle",
    },
  ];
});
</script>

<template>
  <section class="rounded-xl border border-neutral-light bg-white px-6 py-[18px]">
    <div class="flex flex-col items-start gap-4">
      <h2 class="text-sm font-semibold text-text-secondary">
        {{ t(k.control.programming.details.quickConfigurations.title) }}
      </h2>

      <div class="flex flex-wrap items-center gap-3">
        <button
          v-for="configuration in newConfigurations"
          :key="configuration.value"
          :class="
            cn('program-quick-configuration', {
              'program-quick-configuration--active': isActive(configuration.value),
            })
          "
          :aria-pressed="isActive(configuration.value)"
          :data-test-id="DATA_TEST_IDS.components.programEditor.quickConfiguration"
          :data-configuration="configuration.value"
          :disabled="props.disabled"
          type="button"
          @click="emit('toggle', configuration.value)"
        >
          <i
            v-if="isActive(configuration.value)"
            class="pi pi-check !text-[18px] shrink-0"
            data-icon-name="check"
            :data-test-id="DATA_TEST_IDS.components.programEditor.quickConfigurationIcon"
            :data-configuration="configuration.value"
          />
          <component
            :is="configuration.icon"
            v-else
            class="size-[18px] shrink-0 text-text-secondary"
            :data-icon-name="configuration.iconName"
            :data-test-id="DATA_TEST_IDS.components.programEditor.quickConfigurationIcon"
            :data-configuration="configuration.value"
          />
          <span>{{ t(configuration.label) }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.program-quick-configuration {
  @apply flex h-[42px] shrink-0 items-center justify-center gap-2 rounded-full border px-3 py-2 text-sm font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-hover;

  &:not(.program-quick-configuration--active) {
    @apply border-neutral-light bg-white text-text-secondary hover:border-brand hover:text-brand;
  }

  &--active {
    @apply border-brand bg-[#edf4ff] text-brand-hover hover:bg-[#e2edff];
  }
}
</style>
